import { useState, useEffect, useRef } from 'react';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface EPAVehicleDetail {
  mpg_combined: number;
  wh_per_mile: number;
  is_ev: boolean;
  fuel_type: string;
  base_price: number;
  vclass: string;
  drive: string;
  suggested_category: string;
  epa_match_quality: string;
  epa_trim_matched: boolean;
}

export interface VehicleSelectorValue {
  year: number;
  make: string;
  model: string;
  trim: string;
  msrp: number;
  vehicle_category: string;
  category_was_auto_assigned: boolean;
  condition: 'new' | 'used';
  label: string;
  epa_vehicle_id: string | null;
  epa_detail: EPAVehicleDetail | null;
  is_custom: boolean;
  warranty_months: number;
  depreciation_override_5yr: number | null;
  // MSRP-table-sourced fuel type (canonical pre-EPA). 'gasoline' | 'diesel' | 'electric'
  // | 'hybrid' | 'phev' | null. Frontend uses this to drive EV-only UI conditionals
  // (e.g. charging infrastructure cost field) without waiting for EPA enrichment.
  msrp_fuel_type: string | null;
  // Phase 21D-A: Commercial Vehicle Catalog metadata. has_msrp=false signals
  // the dealer-quote MSRP entry path (catalog-only nameplate, no public
  // 2-source MSRP). has_mpg_data=false signals the dealer/manual MPG entry
  // path (no row in mpg_table.py). body_style + gvwr_class auto-populate
  // from the merged backend on trim selection.
  has_msrp: boolean;
  has_mpg_data: boolean;
  body_style: string | null;
  gvwr_class: number | null;
  // v1.44.9: True when the table MSRP is a LABELED ESTIMATE (diesel HD-pickup
  // rows = gas base + a diesel-uplift seed). Drives the "estimated — verify"
  // editor note; the value is always user-overridable.
  msrp_estimated: boolean;
}

interface VehicleSelectorProps {
  value: VehicleSelectorValue;
  onChange: (value: VehicleSelectorValue) => void;
}

// ── Category list ──────────────────────────────────────────────────────────────

// Numeric values sourced: iSeeCars 2025 (depreciation), AAA 2025 (maintenance),
// tire market data March 2026. Mileage baseline: FHWA/KBB 12,000 mi/yr.
// Refresh annually each September.
export const VEHICLE_CATEGORIES: Record<string, {
  display_name: string;
  depreciation_curve: number[];
  residual_floor: number;
  annual_maintenance_base: number;
  annual_repair_base: number;
  tire_interval_months: number;
  tire_set_cost: number;
  insurance_factor: number;
}> = {
  luxury_sedan:     { display_name: 'Luxury Sedan',               depreciation_curve: [0.22,0.13,0.09,0.08,0.07,0.05,0.04,0.03], residual_floor: 0.05, annual_maintenance_base: 1700, annual_repair_base: 1200, tire_interval_months: 42, tire_set_cost: 1300, insurance_factor: 1.4 },
  mainstream_sedan: { display_name: 'Mainstream Sedan',           depreciation_curve: [0.18,0.11,0.08,0.07,0.06,0.05,0.04,0.03], residual_floor: 0.06, annual_maintenance_base: 1100, annual_repair_base: 500,  tire_interval_months: 54, tire_set_cost: 700,  insurance_factor: 1.0 },
  luxury_suv:       { display_name: 'Luxury SUV',                 depreciation_curve: [0.21,0.13,0.09,0.08,0.07,0.05,0.04,0.03], residual_floor: 0.05, annual_maintenance_base: 1800, annual_repair_base: 1300, tire_interval_months: 42, tire_set_cost: 1400, insurance_factor: 1.5 },
  mainstream_suv:   { display_name: 'Mainstream SUV / Crossover', depreciation_curve: [0.16,0.10,0.07,0.06,0.05,0.05,0.04,0.03], residual_floor: 0.08, annual_maintenance_base: 1200, annual_repair_base: 600,  tire_interval_months: 54, tire_set_cost: 900,  insurance_factor: 1.1 },
  truck:            { display_name: 'Pickup Truck',               depreciation_curve: [0.14,0.09,0.07,0.06,0.05,0.05,0.04,0.03], residual_floor: 0.10, annual_maintenance_base: 1400, annual_repair_base: 700,  tire_interval_months: 48, tire_set_cost: 1100, insurance_factor: 1.1 },
  sports_car:       { display_name: 'Sports / Performance Car',   depreciation_curve: [0.16,0.10,0.08,0.07,0.06,0.05,0.04,0.03], residual_floor: 0.08, annual_maintenance_base: 1600, annual_repair_base: 1500, tire_interval_months: 30, tire_set_cost: 1600, insurance_factor: 1.6 },
  ev_mainstream:    { display_name: 'Mainstream EV',              depreciation_curve: [0.23,0.14,0.10,0.08,0.07,0.05,0.04,0.03], residual_floor: 0.04, annual_maintenance_base: 900,  annual_repair_base: 300,  tire_interval_months: 36, tire_set_cost: 900,  insurance_factor: 1.2 },
  ev_luxury:        { display_name: 'Luxury / Performance EV',    depreciation_curve: [0.26,0.15,0.11,0.08,0.07,0.05,0.04,0.03], residual_floor: 0.03, annual_maintenance_base: 1200, annual_repair_base: 600,  tire_interval_months: 36, tire_set_cost: 1400, insurance_factor: 1.5 },
  // Phase 22.1: commercial van — cargo_van, passenger_van, chassis_cab_light body styles.
  commercial_van:   { display_name: 'Commercial Van',             depreciation_curve: [0.13,0.09,0.07,0.06,0.05,0.05,0.04,0.03], residual_floor: 0.10, annual_maintenance_base: 1500, annual_repair_base: 800,  tire_interval_months: 42, tire_set_cost: 1100, insurance_factor: 1.3 },
};

// ── API helpers ────────────────────────────────────────────────────────────────

const API_BASE = (import.meta.env.VITE_API_BASE_URL || '') + '/api';
const EPA_BASE = API_BASE + '/epa';

// ── Shared style helpers ───────────────────────────────────────────────────────

const selectCls =
  'bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-sky-500 w-full';

const inputCls =
  'bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 w-full placeholder:text-slate-500';

const manualInputCls =
  'bg-amber-950/40 border border-amber-600/50 text-slate-100 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 w-full placeholder:text-amber-400/60';

function Skeleton() {
  return <div className="h-9 rounded-lg bg-slate-700 animate-pulse w-full" />;
}

// ── Searchable make combobox ───────────────────────────────────────────────────

interface MakeComboboxProps {
  makes: string[];
  commercialOnlyMakes: string[];
  value: string;
  loading: boolean;
  onChange: (make: string) => void;
}

function MakeCombobox({ makes, commercialOnlyMakes, value, loading, onChange }: MakeComboboxProps) {
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setQuery(value); }, [value]);

  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        if (query !== value) setQuery(value);
      }
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [query, value]);

  const filtered = query.trim()
    ? makes.filter((m) => m.toLowerCase().includes(query.toLowerCase()))
    : makes;

  // Phase 21D-A: split filtered list into passenger (everything not exclusively
  // commercial) and commercial-only groups so the dropdown can render a divider
  // between them. Cosmetic only — selecting a make from either group is valid
  // in any mode (individual or fleet).
  const commercialSet = new Set(commercialOnlyMakes);
  const passengerGroup = filtered.filter((m) => !commercialSet.has(m));
  const commercialGroup = filtered.filter((m) => commercialSet.has(m));
  const showDivider = passengerGroup.length > 0 && commercialGroup.length > 0;

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        className={inputCls}
        value={query}
        placeholder={loading ? 'Loading makes…' : makes.length === 0 ? 'No results' : 'Type to search…'}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); if (e.target.value === '') onChange(''); }}
        onFocus={() => setOpen(true)}
        disabled={loading}
      />
      {open && filtered.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full max-h-56 overflow-y-auto bg-slate-800 border border-slate-600 rounded-lg shadow-xl text-sm text-slate-100">
          {passengerGroup.slice(0, 100).map((m) => (
            <li
              key={m}
              className="px-3 py-2 hover:bg-slate-700 cursor-pointer"
              onMouseDown={() => { setQuery(m); setOpen(false); onChange(m); }}
            >
              {m}
            </li>
          ))}
          {showDivider && (
            <li
              className="px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] cursor-default select-none"
              style={{
                color: 'var(--tco-ink-3)',
                background: 'var(--tco-line-soft)',
                borderTop: '1px solid var(--tco-line)',
                borderBottom: '1px solid var(--tco-line)',
              }}
              aria-hidden="true"
            >
              Commercial · Class 4–7
            </li>
          )}
          {commercialGroup.slice(0, 100).map((m) => (
            <li
              key={m}
              className="px-3 py-2 hover:bg-slate-700 cursor-pointer"
              onMouseDown={() => { setQuery(m); setOpen(false); onChange(m); }}
            >
              {m}
            </li>
          ))}
          {filtered.length > 100 && (
            <li className="px-3 py-2 text-slate-500 italic">{filtered.length - 100} more — type to narrow</li>
          )}
        </ul>
      )}
      {open && filtered.length === 0 && query.trim() !== '' && (
        <div className="absolute z-50 mt-1 w-full bg-slate-800 border border-slate-600 rounded-lg shadow-xl px-3 py-2 text-sm text-slate-400">
          No results found
        </div>
      )}
    </div>
  );
}

// ── Trim type ──────────────────────────────────────────────────────────────────

interface TrimOption {
  trim: string;
  msrp: number;
  fuel_type?: string | null;
  // Phase 21D-A
  has_msrp?: boolean;
  has_mpg_data?: boolean;
  body_style?: string | null;
  gvwr_class?: number | null;
  msrp_estimated?: boolean;
}

// ── Trim display formatter ────────────────────────────────────────────────────

const TRIM_ACRONYMS = new Set([
  'le', 'se', 'xle', 'xse', 'trd', 'awd', 'fwd', 'rwd', 'sr', 'sr5',
  'ev', 'gt', 'st', 'sxt', 'sel', 'ltd', 'lx', 'ex', 'dx', 'hev',
  'phev', 'xl', 'xlt', 'slt', 'lt', 'ls', 'rs', 'ss', 'zl1', 'z71',
  'cs', 'gts', 'srt', 'svr', 'amg', 'jcw', 'sv', 'hse', 'sx', 'ltz',
  'rst', 'zr2', 'at4', 'sl', 'sr', 'sv', 'gt-line', 'xrt', 'sel',
]);

// Special-case display overrides (lowercase key → exact display)
const TRIM_DISPLAY_OVERRIDES: Record<string, string> = {
  'xdrive': 'xDrive',
  'm340i': 'M340i',
  'm440i': 'M440i',
  'm550i': 'M550i',
  'm2': 'M2',
  'm3': 'M3',
  'm4': 'M4',
  'm5': 'M5',
  'm35i': 'M35i',
  'm50': 'M50',
  'm60': 'M60',
  'm60i': 'M60i',
  'm70': 'M70',
  'm240i': 'M240i',
  'm235': 'M235',
  'm850i': 'M850i',
  '4matic': '4MATIC',
  'edrive40': 'eDrive40',
  'edrive50': 'eDrive50',
  'xdrive40': 'xDrive40',
  'xdrive45': 'xDrive45',
  'xdrive60': 'xDrive60',
  'xdrive28i': 'xDrive28i',
  'xdrive30': 'xDrive30',
  'sdrive30i': 'sDrive30i',
  'sdrive40i': 'sDrive40i',
  'r/t': 'R/T',
  'ct4-v': 'CT4-V',
  'ct5-v': 'CT5-V',
  'e-supercharger': 'E-Supercharger',
  'e-ray': 'E-Ray',
  'ex-l': 'EX-L',
};

export function formatTrimDisplay(trim: string): string {
  if (!trim) return '';
  // Split on whitespace while preserving spaces
  return trim.split(/(\s+)/).map((part) => {
    if (/^\s+$/.test(part)) return part;
    const lower = part.toLowerCase();
    if (TRIM_DISPLAY_OVERRIDES[lower]) return TRIM_DISPLAY_OVERRIDES[lower];
    if (TRIM_ACRONYMS.has(lower)) return part.toUpperCase();
    // Title-case: capitalize first letter of each word for unrecognized parts
    return part.charAt(0).toUpperCase() + part.slice(1);
  }).join('');
}

// ── VehicleSelector ────────────────────────────────────────────────────────────

export default function VehicleSelector({ value, onChange }: VehicleSelectorProps) {
  const [supportedYears, setSupportedYears] = useState<number[]>([]);
  const [makes, setMakes] = useState<string[]>([]);
  const [commercialOnlyMakes, setCommercialOnlyMakes] = useState<string[]>([]);
  const [models, setModels] = useState<string[]>([]);
  const [trims, setTrims] = useState<TrimOption[]>([]);

  const [loadingYears, setLoadingYears] = useState(false);
  const [loadingMakes, setLoadingMakes] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingTrims, setLoadingTrims] = useState(false);
  const [loadingEpa, setLoadingEpa] = useState(false);
  const [vehicleType, setVehicleType] = useState('');
  const [warrantyMonths, setWarrantyMonths] = useState(0);
  const [depOverride5yr, setDepOverride5yr] = useState<number | null>(null);

  // Keep a ref to the latest value so async callbacks never use stale closure data
  const valueRef = useRef(value);
  valueRef.current = value;

  // Load supported years on mount
  useEffect(() => {
    setLoadingYears(true);
    fetch(`${API_BASE}/vehicles/years`)
      .then((r) => r.json())
      .then((d) => setSupportedYears(d.years ?? []))
      .catch(() => setSupportedYears([]))
      .finally(() => setLoadingYears(false));
  }, []);

  // Coerce value.year to number — defends against legacy saved scenarios that
  // may have stored model_year as a string (Array.includes uses strict equality
  // and would miss a number→string mismatch otherwise).
  const yearAsNumber = Number(value.year);
  const yearIsSupported = supportedYears.includes(yearAsNumber);

  // Load makes on mount (table-driven, no year dependency)
  useEffect(() => {
    if (value.is_custom) return;
    setLoadingMakes(true);
    fetch(`${API_BASE}/vehicles/makes`)
      .then((r) => r.json())
      .then((d) => {
        setMakes(d.makes ?? []);
        setCommercialOnlyMakes(d.commercial_only_makes ?? []);
      })
      .catch(() => { setMakes([]); setCommercialOnlyMakes([]); })
      .finally(() => setLoadingMakes(false));
  }, [value.is_custom]);

  // Load models when make changes
  useEffect(() => {
    if (value.is_custom || !value.make) { setModels([]); return; }
    setLoadingModels(true);
    setModels([]);
    fetch(`${API_BASE}/vehicles/models?make=${encodeURIComponent(value.make)}`)
      .then((r) => r.json())
      .then((d) => setModels(d.models ?? []))
      .catch(() => setModels([]))
      .finally(() => setLoadingModels(false));
  }, [value.is_custom, value.make]);

  // Load trims when model changes
  useEffect(() => {
    if (value.is_custom || !value.make || !value.model) { setTrims([]); return; }
    setLoadingTrims(true);
    setTrims([]);
    fetch(`${API_BASE}/vehicles/trims?make=${encodeURIComponent(value.make)}&model=${encodeURIComponent(value.model)}`)
      .then((r) => r.json())
      .then((d) => {
        setTrims(d.trims ?? []);
        setVehicleType(d.vehicle_type ?? '');
        setWarrantyMonths(d.warranty_months ?? 36);
        setDepOverride5yr(d.depreciation_override_5yr ?? null);
      })
      .catch(() => { setTrims([]); setVehicleType(''); setWarrantyMonths(36); setDepOverride5yr(null); })
      .finally(() => setLoadingTrims(false));
  }, [value.is_custom, value.make, value.model]);

  function handleModeToggle(isCustom: boolean) {
    onChange({
      ...value,
      is_custom: isCustom,
      make: '',
      model: '',
      trim: '',
      msrp: 0,
      epa_vehicle_id: null,
      epa_detail: null,
      category_was_auto_assigned: false,
      warranty_months: 0,
      label: '',
      has_msrp: true,
      has_mpg_data: true,
      body_style: null,
      gvwr_class: null,
      msrp_estimated: false,
    });
  }

  function handleMake(make: string) {
    onChange({
      ...value,
      make,
      model: '',
      trim: '',
      msrp: 0,
      vehicle_category: '',
      epa_vehicle_id: null,
      epa_detail: null,
      category_was_auto_assigned: false,
      label: [value.year, make].filter(Boolean).join(' '),
      has_msrp: true,
      has_mpg_data: true,
      body_style: null,
      gvwr_class: null,
      msrp_estimated: false,
    });
  }

  function handleModel(e: React.ChangeEvent<HTMLSelectElement>) {
    const model = e.target.value;
    onChange({
      ...value,
      model,
      trim: '',
      msrp: 0,
      vehicle_category: '',
      epa_vehicle_id: null,
      epa_detail: null,
      category_was_auto_assigned: false,
      label: [value.year, value.make, model].filter(Boolean).join(' '),
      has_msrp: true,
      has_mpg_data: true,
      body_style: null,
      gvwr_class: null,
      msrp_estimated: false,
    });
  }

  function handleTrim(e: React.ChangeEvent<HTMLSelectElement>) {
    const rawValue = e.target.value;
    if (rawValue === '') return; // placeholder "— Select Trim —" chosen, ignore
    const selectedTrim = rawValue === '__base__' ? '' : rawValue;
    const trimObj = trims.find((t) => t.trim === selectedTrim);
    const msrp = trimObj?.msrp ?? 0;
    const trimFuelType = trimObj?.fuel_type ?? null;
    // Phase 21D-A: Commercial Vehicle Catalog metadata. has_msrp / has_mpg_data
    // default to true to keep backward-compatible behavior on legacy responses.
    const hasMsrp = trimObj?.has_msrp ?? true;
    const hasMpgData = trimObj?.has_mpg_data ?? true;
    const bodyStyle = trimObj?.body_style ?? null;
    const gvwrClass = trimObj?.gvwr_class ?? null;
    const msrpEstimated = trimObj?.msrp_estimated ?? false;
    // Use formatted display for label only; store raw trim so <select> value matches option values
    const trimDisplay = selectedTrim ? formatTrimDisplay(selectedTrim) : '';
    const newLabel = [value.year, value.make, value.model, trimDisplay].filter(Boolean).join(' ');

    // Warranty only populates when year is in our table, make exists in WARRANTY_TABLE, and trim is selected
    const shouldPopulateWarranty = yearIsSupported && value.make && (selectedTrim || selectedTrim === '');

    // Clear downstream then immediately repopulate from table
    onChange({
      ...value,
      trim: selectedTrim || '(base)',
      msrp,
      vehicle_category: vehicleType || '',
      category_was_auto_assigned: !!vehicleType,
      epa_vehicle_id: null,
      epa_detail: null,
      label: newLabel,
      warranty_months: shouldPopulateWarranty ? warrantyMonths : 0,
      depreciation_override_5yr: depOverride5yr,
      msrp_fuel_type: trimFuelType,
      has_msrp: hasMsrp,
      has_mpg_data: hasMpgData,
      body_style: bodyStyle,
      gvwr_class: gvwrClass,
      msrp_estimated: msrpEstimated,
    });

    // Fire EPA detail lookup in background for MPG/Wh data
    if (value.make && value.model) {
      // Bug 2 fix: pass selected trim so EPA lookup matches the correct option (e.g. M340i vs 330i)
      fireEpaLookup(value.year, value.make, value.model, selectedTrim);
    }
  }

  function fireEpaLookup(year: number, make: string, model: string, selectedTrim?: string) {
    setLoadingEpa(true);

    function fetchOptions(yr: number): Promise<void> {
      const trimParam = selectedTrim ? `&trim=${encodeURIComponent(selectedTrim)}` : '';
      return fetch(`${EPA_BASE}/options?year=${yr}&make=${encodeURIComponent(make)}&model=${encodeURIComponent(model)}${trimParam}`)
        .then((r) => r.json())
        .then((d) => {
          const options = d.options ?? [];
          const optionsMatchQuality: string = d.epa_match_quality ?? 'none';
          if (options.length === 0) {
            // Retry with previous year if no EPA data for requested year
            if (yr === year && yr > 2020) {
              return fetchOptions(yr - 1);
            }
            return;
          }

          // Determine best option and whether trim was matched
          let bestOption = options[0];
          let trimMatched = optionsMatchQuality === 'exact_id';

          if (!trimMatched && selectedTrim) {
            const trimLower = selectedTrim.toLowerCase();
            // Prefer exact match, then startsWith, then includes — avoids "le" matching "xle"
            const exact = options.find((o: { trim: string }) => o.trim?.toLowerCase() === trimLower);
            const starts = !exact && options.find((o: { trim: string }) => o.trim?.toLowerCase().startsWith(trimLower));
            const contains = !exact && !starts && options.find((o: { trim: string }) => o.trim?.toLowerCase().includes(trimLower));
            const match = exact || starts || contains;
            if (match) {
              bestOption = match;
              trimMatched = true;
            }
          }

          const finalMatchQuality = optionsMatchQuality === 'exact_id'
            ? 'exact_id'
            : trimMatched ? optionsMatchQuality : 'model_only';

          const vehicleId = bestOption.option_id;
          return fetch(`${EPA_BASE}/vehicle/${vehicleId}`)
            .then((r) => r.json())
            .then((detail) => {
              const epaDetail: EPAVehicleDetail = {
                mpg_combined: detail.comb08 ?? 0,
                wh_per_mile: detail.wh_per_mile ?? 0,
                is_ev: detail.is_ev ?? false,
                fuel_type: detail.fuelType1 ?? '',
                base_price: detail.base_price || detail.basePrice || 0,
                vclass: detail.VClass ?? '',
                drive: detail.drive ?? '',
                suggested_category: detail.suggested_category ?? '',
                epa_match_quality: finalMatchQuality,
                epa_trim_matched: trimMatched,
              };
              const suggestedCat = detail.suggested_category ?? '';
              const latest = valueRef.current;
              onChange({
                ...latest,
                epa_detail: epaDetail,
                vehicle_category: suggestedCat || latest.vehicle_category,
                category_was_auto_assigned: !!suggestedCat,
              });
            });
        });
    }

    fetchOptions(year)
      .catch(() => { /* EPA lookup is best-effort */ })
      .finally(() => setLoadingEpa(false));
  }

  function handleYear(e: React.ChangeEvent<HTMLSelectElement>) {
    const y = parseInt(e.target.value) || new Date().getFullYear();
    const isSupported = supportedYears.includes(y);
    if (!isSupported) {
      // Unsupported year → switch to Used/Custom mode
      onChange({
        ...value,
        year: y,
        is_custom: true,
        make: '',
        model: '',
        trim: '',
        msrp: 0,
        epa_vehicle_id: null,
        epa_detail: null,
        category_was_auto_assigned: false,
        warranty_months: 0,
        label: String(y),
      });
      return;
    }
    onChange({
      ...value,
      year: y,
      label: value.is_custom
        ? [y, value.make, value.model].filter(Boolean).join(' ')
        : String(y),
      ...(!value.is_custom ? { make: '', model: '', trim: '', msrp: 0, epa_vehicle_id: null, epa_detail: null, warranty_months: 0 } : {}),
    });
  }

  function handleCategory(e: React.ChangeEvent<HTMLSelectElement>) {
    onChange({ ...value, vehicle_category: e.target.value, category_was_auto_assigned: false });
  }

  function handleCondition(cond: 'new' | 'used') {
    onChange({ ...value, condition: cond });
  }

  const categoryEntries = Object.entries(VEHICLE_CATEGORIES);
  const CURRENT_YEAR = new Date().getFullYear();

  return (
    <div className="space-y-3">
      {/* Mode toggle */}
      <div>
        <label className="text-xs font-medium text-slate-400 block mb-1">Vehicle Source</label>
        <div className="flex rounded-lg overflow-hidden border border-slate-700 h-[38px]">
          <button
            type="button"
            onClick={() => handleModeToggle(false)}
            className={`flex-1 text-sm font-medium transition-colors ${
              !value.is_custom ? 'bg-sky-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-slate-200'
            }`}
          >
            New Vehicle
          </button>
          <button
            type="button"
            onClick={() => handleModeToggle(true)}
            className={`flex-1 text-sm font-medium transition-colors ${
              value.is_custom ? 'bg-amber-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-slate-200'
            }`}
          >
            Used / Custom
          </button>
        </div>
        {value.is_custom && (
          <p className="mt-1 text-xs text-amber-400/80">
            {!yearIsSupported
              ? `Model year ${value.year} and older — enter values manually or select ${supportedYears[0] ?? 2026} for auto-population`
              : 'Used vehicle or model not listed above. All fields require manual entry.'}
          </p>
        )}
      </div>

      {/* Year */}
      <div>
        <label className="text-xs font-medium text-slate-400 block mb-1">Model Year</label>
        {value.is_custom ? (
          <input
            type="number"
            className={`${manualInputCls} w-28`}
            value={value.year}
            min={1984}
            max={CURRENT_YEAR + 1}
            onChange={(e) => {
              const y = parseInt(e.target.value) || CURRENT_YEAR;
              onChange({
                ...value,
                year: y,
                warranty_months: 0,
                label: [y, value.make, value.model].filter(Boolean).join(' '),
              });
            }}
          />
        ) : loadingYears ? (
          <Skeleton />
        ) : (
          <select
            className={`${selectCls} w-28`}
            value={value.year}
            onChange={handleYear}
          >
            {supportedYears.length === 0 ? (
              <option value="">No years available</option>
            ) : (
              supportedYears.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))
            )}
          </select>
        )}
        {/* Pre-demo round 2: warning is purely DERIVED state — never persisted. It
            fires only when (a) the user is in New (table-driven) mode, (b) the
            current year is not in the live MSRP table, AND (c) the user has not
            yet resolved a make. Once a make is resolved, the user has a vehicle
            from the current table and the warning is misleading (legacy saved
            scenarios that stored model_year out of sync with the current table
            previously surfaced the warning even though make/model/trim were all
            present and valid). */}
        {!value.is_custom && !yearIsSupported && supportedYears.length > 0 && !value.make && (
          <p className="mt-1 text-xs text-amber-400">
            No vehicle data for this year — use Used/Custom mode
          </p>
        )}
      </div>

      {value.is_custom ? (
        /* ── Custom / Used mode: free-text fields ── */
        <>
          <div>
            <label className="text-xs font-medium text-amber-400 block mb-1">Make</label>
            <input
              type="text"
              className={manualInputCls}
              value={value.make}
              placeholder="e.g. Toyota"
              onChange={(e) => {
                const make = e.target.value;
                onChange({
                  ...value,
                  make,
                  label: [value.year, make, value.model].filter(Boolean).join(' '),
                });
              }}
            />
            <p className="text-xs text-amber-500/60 mt-0.5">Enter value</p>
          </div>
          <div>
            <label className="text-xs font-medium text-amber-400 block mb-1">Model</label>
            <input
              type="text"
              className={manualInputCls}
              value={value.model}
              placeholder="e.g. Camry"
              onChange={(e) => {
                const model = e.target.value;
                onChange({
                  ...value,
                  model,
                  label: [value.year, value.make, model].filter(Boolean).join(' '),
                });
              }}
            />
            <p className="text-xs text-amber-500/60 mt-0.5">Enter value</p>
          </div>
          <div>
            <label className="text-xs font-medium text-amber-400 block mb-1">Trim</label>
            <input
              type="text"
              className={manualInputCls}
              value={value.trim}
              placeholder="e.g. XLE (optional)"
              onChange={(e) => {
                const trim = e.target.value;
                onChange({
                  ...value,
                  trim,
                  label: [value.year, value.make, value.model, trim].filter(Boolean).join(' '),
                });
              }}
            />
            <p className="text-xs text-amber-500/60 mt-0.5">Enter value</p>
          </div>
        </>
      ) : (
        /* ── New Vehicle mode: table-driven dropdowns ── */
        <>
          {/* Make */}
          <div>
            <label className="text-xs font-medium text-slate-400 block mb-1">Make</label>
            {loadingMakes ? <Skeleton /> : (
              <MakeCombobox
                makes={makes}
                commercialOnlyMakes={commercialOnlyMakes}
                value={value.make}
                loading={loadingMakes}
                onChange={handleMake}
              />
            )}
          </div>

          {/* Model */}
          <div>
            <label className="text-xs font-medium text-slate-400 block mb-1">Model</label>
            {loadingModels ? <Skeleton /> : (
              <select
                className={`${selectCls} ${!value.make ? 'opacity-40 cursor-not-allowed' : ''}`}
                value={value.model}
                disabled={!value.make}
                onChange={handleModel}
              >
                <option value="">{!value.make ? 'Select make first' : models.length === 0 ? 'No results' : '— Select Model —'}</option>
                {models.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            )}
          </div>

          {/* Trim with MSRP */}
          <div>
            <label className="text-xs font-medium text-slate-400 block mb-1">Trim</label>
            {loadingTrims ? <Skeleton /> : (
              <select
                className={`${selectCls} ${!value.model ? 'opacity-40 cursor-not-allowed' : ''}`}
                value={value.trim === '(base)' ? '__base__' : value.trim}
                disabled={!value.model}
                onChange={handleTrim}
              >
                <option value="">— Select Trim —</option>
                {trims.map((t) => (
                  <option key={t.trim || '__base__'} value={t.trim || '__base__'}>
                    {t.trim ? formatTrimDisplay(t.trim) : 'Base'}
                  </option>
                ))}
              </select>
            )}
            {loadingEpa && (
              <div className="mt-1 text-xs text-slate-400">Loading EPA fuel economy data…</div>
            )}
            {!loadingEpa && value.epa_detail?.epa_match_quality === 'model_only' && (
              <p className="mt-1 text-xs text-amber-400">
                MPG estimated from base model — verify for your trim
              </p>
            )}
          </div>
        </>
      )}

      {/* Vehicle Category */}
      <div>
        <label className="text-xs font-medium text-slate-400 block mb-1">
          Vehicle Category
          {value.category_was_auto_assigned && (
            <span className="ml-2 text-xs bg-blue-700 text-white px-1.5 py-0.5 rounded font-normal">
              auto
            </span>
          )}
        </label>
        <select
          className={selectCls}
          value={value.vehicle_category}
          onChange={handleCategory}
        >
          <option value="">— Select Category —</option>
          {categoryEntries.map(([key, cat]) => (
            <option key={key} value={key}>{cat.display_name}</option>
          ))}
        </select>
      </div>

      {/* Condition */}
      <div>
        <label className="text-xs font-medium text-slate-400 block mb-1">Condition</label>
        <div className="flex rounded-lg overflow-hidden border border-slate-700 h-[38px]">
          {(['new', 'used'] as const).map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => handleCondition(opt)}
              className={`flex-1 text-sm font-medium transition-colors capitalize ${
                value.condition === opt
                  ? 'bg-sky-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:text-slate-200'
              }`}
            >
              {opt.charAt(0).toUpperCase() + opt.slice(1)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
